import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// Pages where scroll position should be preserved when returning
const SCROLL_PRESERVE_PAGES = ["/cerca-annunci"];

/**
 * Scroll restoration and anchor handling for route changes.
 * - On route change without hash: jump to top (behavior: 'auto')
 * - On route with hash: smooth-scroll to the target element once it's in the DOM
 * - For search pages: restore scroll position when returning from details
 */
const ScrollRestorationHandler = () => {
  const location = useLocation();
  const scrollPositions = useRef<Record<string, number>>({});
  const previousPath = useRef<string | null>(null);

  // Save scroll position when leaving a preserve page
  useEffect(() => {
    const handleScroll = () => {
      const currentPath = location.pathname;
      if (SCROLL_PRESERVE_PAGES.some(p => currentPath.startsWith(p))) {
        scrollPositions.current[currentPath] = window.scrollY;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  useEffect(() => {
    const { hash, pathname } = location;
    const prevPath = previousPath.current;
    previousPath.current = pathname;

    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      const tryScroll = (attempts = 0) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
        if (attempts < 10) {
          // Retry briefly to handle images/fonts/layout shifts
          setTimeout(() => tryScroll(attempts + 1), 80);
        }
      };
      // Defer to next frame so layout can settle
      requestAnimationFrame(() => tryScroll());
    } else if (SCROLL_PRESERVE_PAGES.some(p => pathname.startsWith(p))) {
      // Check if we're returning to a preserved page (e.g., from listing detail)
      const savedPosition = scrollPositions.current[pathname];
      if (savedPosition !== undefined && prevPath && prevPath.startsWith("/annuncio/")) {
        // Restore scroll after a brief delay for content to render
        requestAnimationFrame(() => {
          setTimeout(() => {
            window.scrollTo({ top: savedPosition, behavior: "auto" });
          }, 100);
        });
      } else {
        window.scrollTo({ top: 0, behavior: "auto" });
      }
    } else {
      // Ensure previous scroll isn't restored between routes
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [location.pathname, location.search, location.hash]);

  return null;
};

export default ScrollRestorationHandler;
